/**
 * Search Persistence Hooks
 * SCR-106: Saved Searches & Search History
 *
 * React Query hooks for interacting with the search persistence API.
 * Provides saved searches CRUD and search history management.
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-106 Search Interface
 * @iso27001 A.18.1.4 Privacy and protection of PII
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ============= Types =============

export interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: Record<string, unknown> | null;
  createdAt: string;
}

export interface SavedSearchCreate {
  name: string;
  query: string;
  filters?: Record<string, unknown>;
}

export interface SavedSearchesResponse {
  items: SavedSearch[];
  total: number;
}

export interface SearchHistoryEntry {
  id: string;
  query: string;
  resultCount: number;
  searchedAt: string;
}

export interface SearchHistoryResponse {
  items: SearchHistoryEntry[];
  total: number;
}

// ============= API =============

const API_BASE = '/api/v1';
const TOKEN_KEY = 'myvault_access_token';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function fetchSavedSearches(): Promise<SavedSearchesResponse> {
  const response = await fetch(`${API_BASE}/search/saved`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch saved searches');
  }

  return response.json();
}

async function createSavedSearch(data: SavedSearchCreate): Promise<SavedSearch> {
  const response = await fetch(`${API_BASE}/search/saved`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to save search');
  }

  return response.json();
}

async function deleteSavedSearch(searchId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/search/saved/${searchId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to delete saved search');
  }
}

async function fetchSearchHistory(limit = 20): Promise<SearchHistoryResponse> {
  const response = await fetch(`${API_BASE}/users/search-history?limit=${limit}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch search history');
  }

  return response.json();
}

async function clearSearchHistory(): Promise<void> {
  const response = await fetch(`${API_BASE}/users/search-history`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to clear search history');
  }
}

// ============= Query Keys =============

export const searchKeys = {
  all: ['search'] as const,
  saved: () => [...searchKeys.all, 'saved'] as const,
  history: (limit?: number) => [...searchKeys.all, 'history', limit] as const,
};

// ============= Hooks =============

/**
 * Hook to fetch saved searches
 */
export function useSavedSearches() {
  return useQuery({
    queryKey: searchKeys.saved(),
    queryFn: fetchSavedSearches,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a saved search
 */
export function useCreateSavedSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSavedSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.saved() });
    },
  });
}

/**
 * Hook to delete a saved search
 */
export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSavedSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.saved() });
    },
  });
}

/**
 * Hook to fetch search history
 */
export function useSearchHistory(limit = 20) {
  return useQuery({
    queryKey: searchKeys.history(limit),
    queryFn: () => fetchSearchHistory(limit),
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook to clear search history
 */
export function useClearSearchHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearSearchHistory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.history() });
    },
  });
}
